import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Calendar } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  FilePdf, CalendarBlank, SpinnerGap, DownloadSimple, Trash, Files
} from '@phosphor-icons/react';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';

export const TemplateWithDate = ({ templates, selectedId, onSelect, date, onDateChange, label, testIdPrefix }) => (
  <div className="flex gap-3 items-end">
    <div className="flex-1 space-y-1.5">
      <Label className="text-xs uppercase tracking-[0.2em] font-semibold text-[#A3A3A3]">{label}</Label>
      <Select value={selectedId} onValueChange={onSelect}>
        <SelectTrigger className="bg-[#0A0A0A] border-[#262626] text-[#FAFAFA] rounded-none h-10 focus:border-[#FACC15] focus:ring-0" data-testid={`${testIdPrefix}-select`}>
          <SelectValue placeholder="Выберите шаблон" />
        </SelectTrigger>
        <SelectContent className="bg-[#141414] border-[#262626] text-[#FAFAFA] rounded-none">
          {templates.map(t => (
            <SelectItem key={t.id} value={t.id} className="focus:bg-[#FACC15]/10 focus:text-[#FAFAFA] rounded-none">
              <span className="flex items-center gap-2"><FilePdf size={14} className="text-[#FACC15]" />{t.name}</span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" className="bg-[#0A0A0A] border-[#262626] text-[#FAFAFA] rounded-none h-10 px-3 hover:bg-[#1A1A1A] hover:text-[#FAFAFA] shrink-0" data-testid={`${testIdPrefix}-date`}>
          <CalendarBlank className="mr-2 text-[#A3A3A3]" size={16} />
          {format(date, 'dd.MM.yyyy', { locale: ru })}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 bg-[#141414] border-[#262626]" align="end">
        <Calendar mode="single" selected={date} onSelect={(d) => d && onDateChange(d)} locale={ru} className="bg-[#141414] text-[#FAFAFA]" />
      </PopoverContent>
    </Popover>
  </div>
);

export const TemplateSelector = ({ templates, selectedId, onSelect, testId, isAdmin }) => (
  <div className="space-y-2">
    <Label className="text-xs uppercase tracking-[0.2em] font-semibold text-[#A3A3A3]">Шаблон</Label>
    {templates.length === 0 ? (
      <div className="p-3 border border-dashed border-[#333] bg-[#0A0A0A] text-center">
        <p className="text-xs text-[#525252]">{isAdmin ? 'Загрузите шаблон в Настройках' : 'Нет доступных шаблонов.'}</p>
      </div>
    ) : (
      <Select value={selectedId} onValueChange={onSelect}>
        <SelectTrigger className="bg-[#0A0A0A] border-[#262626] text-[#FAFAFA] rounded-none h-11 focus:border-[#FACC15] focus:ring-0" data-testid={testId}>
          <SelectValue placeholder="Выберите шаблон" />
        </SelectTrigger>
        <SelectContent className="bg-[#141414] border-[#262626] text-[#FAFAFA] rounded-none">
          {templates.map(t => (
            <SelectItem key={t.id} value={t.id} className="focus:bg-[#FACC15]/10 focus:text-[#FAFAFA] rounded-none">
              <span className="flex items-center gap-2"><FilePdf size={14} className="text-[#FACC15]" />{t.name}</span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    )}
  </div>
);

export const HistoryList = ({ items, loading, onDownload, onDelete, nameField = 'full_name', subtitleField = 'passport_id', testPrefix }) => (
  <div className="max-h-[600px] overflow-y-auto">
    {loading ? (
      <div className="flex items-center justify-center py-12"><SpinnerGap size={32} className="animate-spin text-[#A3A3A3]" /></div>
    ) : items.length === 0 ? (
      <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
        <p className="text-sm text-[#525252]">Пусто</p>
      </div>
    ) : (
      <div className="divide-y divide-[#262626]">
        {items.map((item) => (
          <div key={item.id} className="p-3 hover:bg-[#1A1A1A] transition-colors" data-testid={`${testPrefix}-${item.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-[#FAFAFA] truncate">{item[nameField]}</p>
                {subtitleField && <p className="text-xs text-[#A3A3A3] mt-0.5 truncate">{item[subtitleField]}</p>}
                <p className="text-xs text-[#525252] mt-0.5">{new Date(item.created_at).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
              </div>
              <div className="flex gap-1 shrink-0">
                <Button variant="ghost" size="sm" onClick={() => onDownload(item.id, item[nameField])} className="text-[#FACC15] hover:text-[#EAB308] hover:bg-[#FACC15]/10 rounded-none h-7 w-7 p-0">
                  <DownloadSimple size={14} weight="bold" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => onDelete(item.id)} className="text-red-400 hover:text-red-300 hover:bg-red-400/10 rounded-none h-7 w-7 p-0">
                  <Trash size={14} />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

export const FormField = ({ icon: Icon, label, value, onChange, placeholder, testId, type = 'text' }) => (
  <div className="space-y-2">
    <Label className="text-xs uppercase tracking-[0.2em] font-semibold text-[#A3A3A3]">{label}</Label>
    <div className="relative">
      <Icon className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A3A3A3]" size={18} />
      <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} type={type}
        className="pl-10 bg-[#0A0A0A] border-[#262626] text-[#FAFAFA] placeholder:text-[#525252] rounded-none h-11 uppercase focus:border-[#FACC15] focus:ring-0"
        data-testid={testId} />
    </div>
  </div>
);

export const DateField = ({ label, date, onDateChange, testId }) => (
  <div className="space-y-2">
    <Label className="text-xs uppercase tracking-[0.2em] font-semibold text-[#A3A3A3]">{label}</Label>
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" className="w-full justify-start text-left font-normal bg-[#0A0A0A] border-[#262626] text-[#FAFAFA] rounded-none h-11 hover:bg-[#1A1A1A] hover:text-[#FAFAFA]" data-testid={testId}>
          <CalendarBlank className="mr-2 text-[#A3A3A3]" size={18} />
          {format(date, 'dd.MM.yyyy', { locale: ru })}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 bg-[#141414] border-[#262626]" align="start">
        <Calendar mode="single" selected={date} onSelect={(d) => d && onDateChange(d)} locale={ru} className="bg-[#141414] text-[#FAFAFA]" />
      </PopoverContent>
    </Popover>
  </div>
);

export const HistoryPanel = ({ title, count, icon: Icon = Files, children }) => (
  <div className="border border-[#262626] bg-[#141414] h-fit">
    <div className="p-4 border-b border-[#262626] flex items-center gap-2">
      <Icon size={18} className="text-[#A3A3A3]" />
      <h2 className="text-xs uppercase tracking-[0.2em] font-semibold text-[#A3A3A3]">{title}</h2>
      <span className="ml-auto text-xs text-[#525252]">{count}</span>
    </div>
    {children}
  </div>
);
